// Customers + customer prices registers + create/edit (BUILD_SPEC.md §5.7, §11
// "Registers with fast fuzzy search + filters"). Plan.md G23. Mirrors features/
// purchasing-intake/SupplierRegister.tsx and SupplierContractRegister.tsx's modal
// create/edit convention (this feature's own copy per S0 ownership).
import { useMemo, useState } from 'react';
import { Alert, Badge, Button, Group, Modal, Paper, Select, Stack, Switch, Table, Tabs, Text, TextInput, Title } from '@mantine/core';
import { useForm } from '@mantine/form';
import { IconAlertCircle, IconPlus, IconUsers, IconTag } from '@tabler/icons-react';
import { ApiRequestError } from '../../api/client';
import { CONSOLIDATION_PERIODS, INVOICING_MODES, type ConsolidationPeriod, type CustomerCreateInput, type InvoicingMode } from '@zebra/core';
import { formatSastDate, sastDayEnd, sastDayStart, todayIsoDate } from '../../lib/sastDate';
import { centsToRand, randToCents } from './format';
import {
  type CustomerPriceWriteInput,
  useCreateCustomer,
  useCreateCustomerPrice,
  useCustomerPrices,
  useCustomers,
  useUpdateCustomer,
  useUpdateCustomerPrice,
} from './api';
import { CustomerSelect } from './components/CustomerSelect';
import { ProductSelect } from './components/ProductSelect';
import { CustomerName, ProductName } from './components/EntityNames';

/** Human labels for `InvoicingMode` (Item 5a) — mirrors features/purchasing-intake/
 * common.ts's `PRICING_BASIS_LABELS` pattern so plant/office staff never see the raw
 * enum tokens (`PER_LOAD`/`CONSOLIDATED`) in the create/edit form or the register. */
const INVOICING_MODE_LABELS: Record<InvoicingMode, string> = {
  PER_LOAD: 'Per load',
  CONSOLIDATED: 'Consolidated (monthly)',
};

export function CustomersAndPrices() {
  return (
    <Stack gap="lg">
      <Stack gap={2}>
        <Title order={2}>Customers &amp; prices</Title>
        <Text c="dimmed">Customer master data, invoicing mode and per-product contract prices.</Text>
      </Stack>
      <Tabs defaultValue="customers">
        <Tabs.List>
          <Tabs.Tab value="customers" leftSection={<IconUsers size={16} />}>
            Customers
          </Tabs.Tab>
          <Tabs.Tab value="prices" leftSection={<IconTag size={16} />}>
            Prices
          </Tabs.Tab>
        </Tabs.List>
        <Tabs.Panel value="customers" pt="md">
          <CustomerRegister />
        </Tabs.Panel>
        <Tabs.Panel value="prices" pt="md">
          <CustomerPriceRegister />
        </Tabs.Panel>
      </Tabs>
    </Stack>
  );
}

// ---------------------------------------------------------------------------
// Customers
// ---------------------------------------------------------------------------

interface CustomerRow {
  id: string;
  code: string;
  name: string;
  vatNumber: string | null;
  email: string | null;
  deliveryAddress: string | null;
  paymentTerms: string | null;
  invoicingMode: InvoicingMode;
  consolidationPeriod: ConsolidationPeriod | null;
  xeroContactId: string | null;
  isActive: boolean;
}

interface CustomerFormValues {
  code: string;
  name: string;
  vatNumber: string;
  email: string;
  deliveryAddress: string;
  paymentTerms: string;
  invoicingMode: InvoicingMode;
  consolidationPeriod: ConsolidationPeriod | '';
  isActive: boolean;
}

function toCustomerFormValues(row?: CustomerRow): CustomerFormValues {
  return {
    code: row?.code ?? '',
    name: row?.name ?? '',
    vatNumber: row?.vatNumber ?? '',
    email: row?.email ?? '',
    deliveryAddress: row?.deliveryAddress ?? '',
    paymentTerms: row?.paymentTerms ?? '',
    invoicingMode: row?.invoicingMode ?? 'PER_LOAD',
    consolidationPeriod: row?.consolidationPeriod ?? '',
    isActive: row?.isActive ?? true,
  };
}

function toCustomerInput(values: CustomerFormValues): CustomerCreateInput {
  return {
    code: values.code.trim(),
    name: values.name.trim(),
    vatNumber: values.vatNumber.trim() || null,
    email: values.email.trim() || null,
    deliveryAddress: values.deliveryAddress.trim() || null,
    paymentTerms: values.paymentTerms.trim() || null,
    invoicingMode: values.invoicingMode,
    consolidationPeriod: values.invoicingMode === 'CONSOLIDATED' ? (values.consolidationPeriod || null) : null,
    isActive: values.isActive,
  };
}

function CustomerFormModal({ opened, onClose, editing }: { opened: boolean; onClose: () => void; editing: CustomerRow | null }) {
  const create = useCreateCustomer();
  const update = useUpdateCustomer(editing?.id ?? '');
  const mutation = editing ? update : create;

  const form = useForm<CustomerFormValues>({
    initialValues: toCustomerFormValues(editing ?? undefined),
    validate: {
      code: (v) => (v.trim() ? null : 'Code is required'),
      name: (v) => (v.trim() ? null : 'Name is required'),
      email: (v) => (v.trim() === '' || /\S+@\S+\.\S+/.test(v) ? null : 'Invalid email'),
      consolidationPeriod: (v, values) => (values.invoicingMode === 'CONSOLIDATED' && !v ? 'Required for consolidated invoicing' : null),
    },
  });

  function handleSubmit(values: CustomerFormValues) {
    mutation.mutate(toCustomerInput(values), { onSuccess: () => onClose() });
  }

  return (
    <Modal opened={opened} onClose={onClose} title={editing ? `Edit customer ${editing.code}` : 'New customer'} centered size="lg">
      <form onSubmit={form.onSubmit(handleSubmit)}>
        <Stack gap="sm">
          {mutation.isError && (
            <Alert color="red" icon={<IconAlertCircle size={16} />}>
              {mutation.error instanceof ApiRequestError ? mutation.error.message : 'Failed to save customer'}
            </Alert>
          )}
          <TextInput label="Code" required {...form.getInputProps('code')} />
          <TextInput label="Name" required {...form.getInputProps('name')} />
          <TextInput label="VAT number" {...form.getInputProps('vatNumber')} />
          <TextInput label="Email" type="email" {...form.getInputProps('email')} />
          <TextInput label="Delivery address" {...form.getInputProps('deliveryAddress')} />
          <TextInput label="Payment terms" description="Free text shown on invoices, e.g. '30 days'." placeholder="e.g. 30 days" {...form.getInputProps('paymentTerms')} />
          <Select
            label="Invoicing mode"
            description="Per load invoices each dispatch as it happens. Consolidated batches every dispatch in the period into one invoice."
            data={INVOICING_MODES.map((m) => ({ value: m, label: INVOICING_MODE_LABELS[m] }))}
            required
            {...form.getInputProps('invoicingMode')}
          />
          {form.values.invoicingMode === 'CONSOLIDATED' && (
            <Select
              label="Consolidation period"
              description="How often a consolidated invoice is raised for this customer."
              data={CONSOLIDATION_PERIODS.map((p) => ({ value: p, label: p }))}
              required
              {...form.getInputProps('consolidationPeriod')}
            />
          )}
          <Switch
            label="Active"
            description="Inactive customers are hidden from new sales orders and dispatches, but their existing documents are unaffected."
            {...form.getInputProps('isActive', { type: 'checkbox' })}
          />
          <Group justify="flex-end" mt="sm">
            <Button variant="default" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" loading={mutation.isPending}>
              {editing ? 'Save changes' : 'Create customer'}
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}

function CustomerRegister() {
  const { data, isLoading, isError } = useCustomers();
  const [modalOpened, setModalOpened] = useState(false);
  const [editing, setEditing] = useState<CustomerRow | null>(null);

  const rows = useMemo(() => data?.items ?? [], [data]);

  function openCreate() {
    setEditing(null);
    setModalOpened(true);
  }
  function openEdit(row: CustomerRow) {
    setEditing(row);
    setModalOpened(true);
  }

  return (
    <Stack gap="md">
      <Group justify="flex-end">
        <Button leftSection={<IconPlus size={16} />} onClick={openCreate}>
          New customer
        </Button>
      </Group>

      {isError && (
        <Alert color="red" icon={<IconAlertCircle size={16} />}>
          Failed to load customers.
        </Alert>
      )}

      <Paper withBorder radius="md">
        <Table.ScrollContainer minWidth={640}>
          <Table striped highlightOnHover verticalSpacing="sm">
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Code</Table.Th>
                <Table.Th>Name</Table.Th>
                <Table.Th>Invoicing</Table.Th>
                <Table.Th>Contact</Table.Th>
                <Table.Th>Status</Table.Th>
                <Table.Th />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {rows.map((row) => (
                <Table.Tr key={row.id}>
                  <Table.Td>{row.code}</Table.Td>
                  <Table.Td>{row.name}</Table.Td>
                  <Table.Td>
                    {INVOICING_MODE_LABELS[row.invoicingMode]}
                    {row.consolidationPeriod ? ` · ${row.consolidationPeriod}` : ''}
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm">{row.email ?? '—'}</Text>
                  </Table.Td>
                  <Table.Td>
                    <Badge color={row.isActive ? 'brandGreen' : 'gray'} variant="light">
                      {row.isActive ? 'Active' : 'Inactive'}
                    </Badge>
                  </Table.Td>
                  <Table.Td>
                    <Button variant="subtle" size="xs" onClick={() => openEdit(row)}>
                      Edit
                    </Button>
                  </Table.Td>
                </Table.Tr>
              ))}
              {!isLoading && rows.length === 0 && (
                <Table.Tr>
                  <Table.Td colSpan={6}>
                    <Text c="dimmed" ta="center" py="md">
                      No customers yet.
                    </Text>
                  </Table.Td>
                </Table.Tr>
              )}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      </Paper>

      <CustomerFormModal key={editing?.id ?? 'new'} opened={modalOpened} onClose={() => setModalOpened(false)} editing={editing} />
    </Stack>
  );
}

// ---------------------------------------------------------------------------
// Customer prices
// ---------------------------------------------------------------------------

// `validFrom`/`validTo` are typed `Date | string` here (not just `string`) because
// `CustomerPriceResponse.validFrom` is `z.coerce.date()`'s OUTPUT type (`Date`) —
// but the browser only ever sees the raw JSON.parse'd wire value (a plain ISO
// string), never actually re-parsed through zod (see features/purchasing-intake/
// common.ts's `asDate` header comment for the identical situation elsewhere in
// this app). Accepting either keeps this interface assignable from both a real
// `CustomerPriceResponse` row and a freshly-typed local value.
interface CustomerPriceRow {
  id: string;
  customerId: string;
  productId: string;
  priceCentsPerTonne: string | null;
  priceCentsPerUnit: string | null;
  validFrom: Date | string;
  validTo: Date | string | null;
}

type PriceMode = 'TONNE' | 'UNIT';

interface CustomerPriceFormValues {
  customerId: string;
  productId: string;
  priceMode: PriceMode;
  priceRand: string;
  validFrom: string;
  validTo: string;
}

function toPriceFormValues(row?: CustomerPriceRow): CustomerPriceFormValues {
  const mode: PriceMode = row?.priceCentsPerUnit != null ? 'UNIT' : 'TONNE';
  return {
    customerId: row?.customerId ?? '',
    productId: row?.productId ?? '',
    priceMode: mode,
    priceRand: mode === 'UNIT' ? centsToRand(row?.priceCentsPerUnit ?? null) : centsToRand(row?.priceCentsPerTonne ?? null),
    // SAST calendar dates, not `.toISOString().slice(0, 10)` UTC truncation (spec §4
    // item 7, Amendment 2026-07-10): a stored SAST day-start instant is 22:00Z on the
    // PREVIOUS UTC day, so UTC truncation would prefill the edit form one day early.
    validFrom: row?.validFrom ? formatSastDate(row.validFrom) : todayIsoDate(),
    validTo: row?.validTo ? formatSastDate(row.validTo) : '',
  };
}

function toPriceInput(values: CustomerPriceFormValues): CustomerPriceWriteInput {
  const cents = randToCents(values.priceRand);
  return {
    customerId: values.customerId,
    productId: values.productId,
    priceCentsPerTonne: values.priceMode === 'TONNE' ? cents : null,
    priceCentsPerUnit: values.priceMode === 'UNIT' ? cents : null,
    // The validity window is inclusive of both whole SAST business days (spec §4
    // item 7, Amendment 2026-07-10) — `pickCurrentCustomerPrice` (api.ts) matches
    // `validFrom <= asOf && validTo >= asOf`, so a bare UTC-midnight `new
    // Date('YYYY-MM-DD')` would leave the price inapplicable for the first two SAST
    // hours of its first day and cut it off at 02:00 SAST on its stated last day.
    validFrom: sastDayStart(values.validFrom),
    validTo: values.validTo ? sastDayEnd(values.validTo) : null,
  };
}

function CustomerPriceFormModal({ opened, onClose, editing }: { opened: boolean; onClose: () => void; editing: CustomerPriceRow | null }) {
  const create = useCreateCustomerPrice();
  const update = useUpdateCustomerPrice(editing?.id ?? '');
  const mutation = editing ? update : create;

  const form = useForm<CustomerPriceFormValues>({
    initialValues: toPriceFormValues(editing ?? undefined),
    validate: {
      customerId: (v) => (v ? null : 'Customer is required'),
      productId: (v) => (v ? null : 'Product is required'),
      priceRand: (v) => (v.trim() ? null : 'Price is required'),
    },
  });

  function handleSubmit(values: CustomerPriceFormValues) {
    mutation.mutate(toPriceInput(values), { onSuccess: () => onClose() });
  }

  return (
    <Modal opened={opened} onClose={onClose} title={editing ? 'Edit customer price' : 'New customer price'} centered size="lg">
      <form onSubmit={form.onSubmit(handleSubmit)}>
        <Stack gap="sm">
          {mutation.isError && (
            <Alert color="red" icon={<IconAlertCircle size={16} />}>
              {mutation.error instanceof ApiRequestError ? mutation.error.message : 'Failed to save price'}
            </Alert>
          )}
          <CustomerSelect label="Customer" required value={form.values.customerId} onChange={(id) => form.setFieldValue('customerId', id)} />
          <ProductSelect label="Product" required value={form.values.productId} onChange={(id) => form.setFieldValue('productId', id)} />
          <Select
            label="Price basis"
            description="Per tonne prices scale with mass; per unit uses the product's own base unit (kg or count)."
            data={[
              { value: 'TONNE', label: 'Per tonne' },
              { value: 'UNIT', label: 'Per unit (base UOM)' },
            ]}
            {...form.getInputProps('priceMode')}
          />
          <TextInput
            label={form.values.priceMode === 'TONNE' ? 'Price per tonne (ZAR, ex VAT)' : 'Price per unit (ZAR, ex VAT)'}
            required
            {...form.getInputProps('priceRand')}
          />
          <Group grow>
            <TextInput
              label="Valid from"
              description="This price is picked up automatically for dispatches on or after this date."
              type="date"
              required
              {...form.getInputProps('validFrom')}
            />
            <TextInput
              label="Valid to (optional)"
              description="Leave blank to keep the price active until a newer one starts."
              type="date"
              {...form.getInputProps('validTo')}
            />
          </Group>
          <Group justify="flex-end" mt="sm">
            <Button variant="default" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" loading={mutation.isPending}>
              {editing ? 'Save changes' : 'Create price'}
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}

function CustomerPriceRegister() {
  const { data, isLoading, isError } = useCustomerPrices();
  const [modalOpened, setModalOpened] = useState(false);
  const [editing, setEditing] = useState<CustomerPriceRow | null>(null);

  const rows = data?.items ?? [];

  function openCreate() {
    setEditing(null);
    setModalOpened(true);
  }
  function openEdit(row: CustomerPriceRow) {
    setEditing(row);
    setModalOpened(true);
  }

  return (
    <Stack gap="md">
      <Group justify="flex-end">
        <Button leftSection={<IconPlus size={16} />} onClick={openCreate}>
          New price
        </Button>
      </Group>

      {isError && (
        <Alert color="red" icon={<IconAlertCircle size={16} />}>
          Failed to load customer prices.
        </Alert>
      )}

      <Paper withBorder radius="md">
        <Table.ScrollContainer minWidth={640}>
          <Table striped highlightOnHover verticalSpacing="sm">
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Customer</Table.Th>
                <Table.Th>Product</Table.Th>
                <Table.Th>Price</Table.Th>
                <Table.Th>Valid from</Table.Th>
                <Table.Th>Valid to</Table.Th>
                <Table.Th />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {rows.map((row) => (
                <Table.Tr key={row.id}>
                  <Table.Td>
                    <CustomerName id={row.customerId} />
                  </Table.Td>
                  <Table.Td>
                    <ProductName id={row.productId} />
                  </Table.Td>
                  <Table.Td>
                    {row.priceCentsPerTonne != null ? `R${centsToRand(row.priceCentsPerTonne)} / t` : `R${centsToRand(row.priceCentsPerUnit)} / unit`}
                  </Table.Td>
                  {/* SAST business dates, not the browser's own timezone — a stored SAST
                      day-start instant renders one day early on a non-SAST host. */}
                  <Table.Td>{formatSastDate(row.validFrom)}</Table.Td>
                  <Table.Td>{row.validTo ? formatSastDate(row.validTo) : '—'}</Table.Td>
                  <Table.Td>
                    <Button variant="subtle" size="xs" onClick={() => openEdit(row)}>
                      Edit
                    </Button>
                  </Table.Td>
                </Table.Tr>
              ))}
              {!isLoading && rows.length === 0 && (
                <Table.Tr>
                  <Table.Td colSpan={6}>
                    <Text c="dimmed" ta="center" py="md">
                      No customer prices yet.
                    </Text>
                  </Table.Td>
                </Table.Tr>
              )}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      </Paper>

      <CustomerPriceFormModal key={editing?.id ?? 'new'} opened={modalOpened} onClose={() => setModalOpened(false)} editing={editing} />
    </Stack>
  );
}
